/**
 * Client orchestration — solo (offline) + online 1v1 (Tikron).
 *
 * Screen flow (solo):
 *   START → (camera + tracker init) → CALIBRATE → COUNTDOWN → ROUND → RESULT
 * Screen flow (online): the SERVER drives the phase machine; the client mirrors
 *   it — LOBBY → CALIBRATE (local) → COUNTDOWN → ROUND (render server foods) →
 *   ROUNDRESULT → MATCHRESULT. Foods, scores, and eating are server-authoritative;
 *   the client only sends `mouth` intents and plays predictive FX.
 * `?mouse=1` skips camera/calibration and drives the mouth with the pointer — it
 *   works in BOTH modes (two desktop tabs with ?mouse=1 = an online match).
 *
 * The game loop is delta-time based end to end (iOS Low Power Mode caps rAF at
 * 30fps). Solo food physics run on a fixed 50ms accumulator; online rendering
 * extrapolates the last server snapshot by wall time since it arrived.
 */

import type { Ticker } from "pixi.js";
import { BURN_MS, COUNTDOWN_MS, FILTERS, isFilterId, ROUND_MS, ROUNDS_TO_WIN, TICK_MS } from "./types.js";
import type { FilterId, MatchPhase, MatchState, RoundState, SimEvent } from "./types.js";
import { GameSim } from "./sim.js";
import { GameRenderer } from "./render.js";
import { startCamera, waitForDimensions } from "./camera.js";
import { Calibrator, createFaceTracker, MouthFSM } from "./face.js";
import type { FaceTracker } from "./face.js";
import { FaceMouthSource, MouseMouthSource } from "./mouth-sources.js";
import type { MouthSource } from "./mouth-sources.js";
import { NetClient, normalizeRoomCode, type NetHandlers } from "./net.js";
import { RtcLink, type ConnectionState, type RtcSignal } from "./rtc.js";
import { PenaltyStage } from "./penalty.js";
import { acquireWakeLock, reacquireWakeLockIfWanted, releaseWakeLock } from "./wakelock.js";
import { androidChromeIntentUrl, isAndroidUA, isInAppBrowser } from "./inapp-browser.js";
import * as sfx from "./sfx.js";

const CALIB_MS = 4000;
const FACE_LOST_MS = 2000;

type Phase = "idle" | "calibrate" | "countdown" | "round" | "result";
type Mode = "solo" | "online";

// --- DOM ---
function must<T extends HTMLElement>(id: string): T {
  const el = document.getElementById(id);
  if (!el) throw new Error(`missing #${id}`);
  return el as T;
}

const gameEl = must<HTMLDivElement>("game");
const hud = must<HTMLElement>("hud");
const scoreEl = must<HTMLElement>("score");
const myWinsEl = must<HTMLElement>("my-wins");
const timebarFill = must<HTMLElement>("timebar-fill");
const burnIndicator = must<HTMLElement>("burn-indicator");
const facelost = must<HTMLElement>("facelost");
const popupsEl = must<HTMLElement>("popups");
const btnStart = must<HTMLButtonElement>("btn-start");
const calibText = must<HTMLElement>("calib-text");
const calibGauge = must<HTMLElement>("calib-gauge");
const calibFill = must<HTMLElement>("calib-fill");
const countdownNum = must<HTMLElement>("countdown-num");
const resultScore = must<HTMLElement>("result-score");
const btnAgain = must<HTMLButtonElement>("btn-again");
const btnRecalib = must<HTMLButtonElement>("btn-recalib");
const statusEl = must<HTMLElement>("status");
const perfEl = must<HTMLElement>("perf");

// online DOM
const btnHost = must<HTMLButtonElement>("btn-host");
const btnJoinCode = must<HTMLButtonElement>("btn-joincode");
const btnRandom = must<HTMLButtonElement>("btn-random");
const btnTryon = must<HTMLButtonElement>("btn-tryon");
const btnShare = must<HTMLButtonElement>("btn-share");
const joinCodeInput = must<HTMLInputElement>("join-code-input");
const btnJoinConfirm = must<HTMLButtonElement>("btn-join-confirm");
const btnJoinBack = must<HTMLButtonElement>("btn-join-back");
const lobbyText = must<HTMLElement>("lobby-text");
const lobbyCode = must<HTMLElement>("lobby-code");
const lobbyHint = must<HTMLElement>("lobby-hint");
const oppHud = must<HTMLElement>("opp-hud");
const oppName = must<HTMLElement>("opp-name");
const oppScore = must<HTMLElement>("opp-score");
const oppWins = must<HTMLElement>("opp-wins");
const rrTitle = must<HTMLElement>("rr-title");
const rrScore = must<HTMLElement>("rr-score");
const rrWins = must<HTMLElement>("rr-wins");
const mrTitle = must<HTMLElement>("mr-title");
const mrWins = must<HTMLElement>("mr-wins");
const mrOppRematch = must<HTMLElement>("mr-opp-rematch");
const btnRematch = must<HTMLButtonElement>("btn-rematch");
const btnExit = must<HTMLButtonElement>("btn-exit");
const btnLobbyCancel = must<HTMLButtonElement>("btn-lobby-cancel");

// Phase 3: WebRTC video/voice DOM
const rtcPip = must<HTMLElement>("rtc-pip");
const rtcVideo = must<HTMLVideoElement>("rtc-video");
const rtcStatus = must<HTMLElement>("rtc-status");
const rtcPaused = must<HTMLElement>("rtc-paused");
const rtcUnmuteChip = must<HTMLButtonElement>("rtc-unmute-chip");
const btnMic = must<HTMLButtonElement>("btn-mic");

// Phase 4: penalty AR filter stage DOM
/**
 * Builds one filter strip's buttons from FILTERS (never hardcoded in
 * index.html, so adding a filter there can't silently omit its button) and
 * wires each to `onSelect`. `includeNone` prepends a "🚫 없음" clear button
 * (try-on only — the matchResult winner has no "clear" concept, just picks).
 */
function buildFilterStrip(
  container: HTMLElement,
  includeNone: boolean,
  onSelect: (filter: FilterId | null) => void,
): HTMLButtonElement[] {
  const defs: { id: string; emoji: string; label: string }[] = includeNone
    ? [{ id: "", emoji: "🚫", label: "없음" }, ...FILTERS]
    : [...FILTERS];
  const buttons = defs.map((f) => {
    const btn = document.createElement("button");
    btn.className = "filter-btn";
    btn.dataset["filter"] = f.id;
    const label = document.createElement("span");
    label.textContent = f.label;
    btn.append(f.emoji, label);
    btn.addEventListener("click", () => onSelect(isFilterId(f.id) ? f.id : null));
    container.appendChild(btn);
    return btn;
  });
  // Desktop convenience: the strip is a horizontal scroller now (11+ filters
  // no longer fit wrapped) — a plain vertical mouse wheel over it has nothing
  // to scroll otherwise. Drag-to-scroll is left to the native scrollbar/trackpad.
  container.addEventListener(
    "wheel",
    (e) => {
      if (e.deltaY === 0) return;
      e.preventDefault();
      container.scrollLeft += e.deltaY;
    },
    { passive: false },
  );
  return buttons;
}

function highlightSelected(buttons: HTMLButtonElement[], filter: FilterId | null): void {
  for (const btn of buttons) {
    const selected = (btn.dataset["filter"] || null) === filter;
    btn.classList.toggle("selected", selected);
    if (selected) btn.scrollIntoView({ inline: "nearest", block: "nearest" });
  }
}

const mrFilterStrip = must<HTMLElement>("mr-filter-strip");
const filterButtons = buildFilterStrip(mrFilterStrip, false, (filter) => {
  if (filter) net?.send("penalty", { filter });
});
const penaltyStageEl = must<HTMLElement>("penalty-stage");
const penaltyVideo = must<HTMLVideoElement>("penalty-video");
const penaltyCanvas = must<HTMLCanvasElement>("penalty-canvas");
const penaltyHint = must<HTMLElement>("penalty-hint");
const penaltyPlaceholder = must<HTMLElement>("penalty-placeholder");
const btnSnapshot = must<HTMLButtonElement>("btn-snapshot");
const snapshotFallback = must<HTMLElement>("snapshot-fallback");
const snapshotImg = must<HTMLImageElement>("snapshot-img");
const snapshotDownload = must<HTMLAnchorElement>("snapshot-download");
const btnSnapshotClose = must<HTMLButtonElement>("btn-snapshot-close");

// Try-on mode DOM (fully local — no room/server involved)
const tryonFilterStrip = must<HTMLElement>("tryon-filter-strip");
const tryonStageEl = must<HTMLElement>("tryon-stage");
const tryonVideo = must<HTMLVideoElement>("tryon-video");
const tryonCanvas = must<HTMLCanvasElement>("tryon-canvas");
const tryonHint = must<HTMLElement>("tryon-hint");
const btnTryonSnapshot = must<HTMLButtonElement>("btn-tryon-snapshot");
const btnTryonBack = must<HTMLButtonElement>("btn-tryon-back");
const tryonFilterButtons = buildFilterStrip(tryonFilterStrip, true, (filter) => {
  tryonFilter = filter;
  highlightSelected(tryonFilterButtons, filter);
  tryonStage?.setFilter(filter);
});

// --- Phase 5: in-app browser escape banner ---
// KakaoTalk/Instagram/Facebook/Line/Naver in-app webviews have known
// getUserMedia/WebRTC quirks. Banner-only — never blocks the app. Dismissal
// persists for the browser session via sessionStorage (survives a reload,
// clears on a fresh tab) so it doesn't nag on every visit to the start screen.
const INAPP_DISMISS_KEY = "nyamduel-inapp-dismissed";
const inappBanner = must<HTMLElement>("inapp-banner");
const inappChromeLink = must<HTMLAnchorElement>("inapp-chrome-link");
const btnInappCopy = must<HTMLButtonElement>("btn-inapp-copy");
const btnInappDismiss = must<HTMLButtonElement>("btn-inapp-dismiss");

function dismissInappBanner(): void {
  inappBanner.classList.add("hidden");
  try {
    sessionStorage.setItem(INAPP_DISMISS_KEY, "1");
  } catch {
    // Private-mode Safari etc. can throw on storage access — the dismissal
    // just won't survive a reload, which is a harmless degradation.
  }
}

function initInappBanner(): void {
  let dismissed = false;
  try {
    dismissed = sessionStorage.getItem(INAPP_DISMISS_KEY) === "1";
  } catch {
    dismissed = false;
  }
  if (dismissed || !isInAppBrowser()) return;
  if (isAndroidUA()) {
    inappChromeLink.href = androidChromeIntentUrl(location.href);
    inappChromeLink.classList.remove("hidden");
  }
  inappBanner.classList.remove("hidden");
}

btnInappDismiss.addEventListener("click", dismissInappBanner);
btnInappCopy.addEventListener("click", () => {
  const url = location.href;
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(url).then(
      () => toast("링크를 복사했어요 📋"),
      () => window.prompt("아래 링크를 복사하세요 👇", url),
    );
  } else {
    window.prompt("아래 링크를 복사하세요 👇", url);
  }
});
initInappBanner();

// --- state ---
const queryParams = new URLSearchParams(location.search);
const mouseMode = queryParams.get("mouse") === "1";
const perfMode = queryParams.get("perf") === "1" || queryParams.has("debug");
const sim = new GameSim();
const renderer = new GameRenderer();

let mouthSource: MouthSource | null = null;
let tracker: FaceTracker | null = null;
let video: HTMLVideoElement | null = null;
let camStream: MediaStream | null = null;
let calibrator = new Calibrator();

let mode: Mode = "solo";
let phase: Phase = "idle";
let accumulator = 0;
let calibStart = 0;
let faceLostSince = -1;

// online state
let net: NetClient | null = null;
let serverState: MatchState | null = null;
let serverPhase: MatchPhase | null = null;
let lobbyRole: "host" | "join" | "random" = "host";
let displayCode: string | null = null;
let countdownStartWall = 0;
let prevLocalCloseSeq = 0;
let mouthSendTimer = -1;

// Phase 3: WebRTC video/voice state
let rtcLink: RtcLink | null = null;
let iceServers: RTCIceServer[] = [{ urls: "stun:stun.cloudflare.com:3478" }];
let micOn = true;
/** Set while `RtcLink.create()` is in flight — guards against a second `ensureRtcLink()` racing it. */
let rtcLinkCreating: Promise<void> | null = null;
/** Bumped on every teardown/new creation so a superseded in-flight create() discards its result. */
let rtcEpoch = 0;
/** Relayed signaling that arrived before the (async) RtcLink existed; replayed once it's ready. */
let pendingRtcMessages: { payload: RtcSignal; from: string }[] = [];
/** Guards the one-shot "connected" diagnostics log per connection. */
let loggedConnectedPair = false;
/** Captured whenever attachRemoteStream fires — reused by the winner's penalty stage. */
let remoteStream: MediaStream | null = null;

// Phase 4: penalty AR filter stage state
let penaltyStage: PenaltyStage | null = null;
/** True only while the LOSER's own penalty stage is up (keeps local detection running past matchResult). */
let loserPenaltyActive = false;
/** Bumped on every teardown so an in-flight winner-stage setup (async tracker create) can discard itself. */
let penaltyEpoch = 0;
/** Guards startWinnerPenaltyStage() against re-entrant calls while its async setup is in flight. */
let winnerStageStarting = false;
/** Guards startLoserPenaltyStage() the same way — it can be re-invoked on every matchResult
 *  broadcast (e.g. the winner switching filters) while a previous call is still awaiting dims. */
let loserStageStarting = false;
let snapshotObjectUrl: string | null = null;

// Try-on mode state — fully local, no room/server involved at all.
let tryonStage: PenaltyStage | null = null;
/** Bumped by teardownTryOn() so an in-flight startTryOnStage() (awaiting video
 *  dimensions) discards itself instead of resurrecting a stage after "back" was tapped. */
let tryonEpoch = 0;
/** Gates the local tracker update in the main loop, like loserPenaltyActive. */
let tryonActive = false;
/** Pure local selection — never sent anywhere. */
let tryonFilter: FilterId | null = null;
/** Guards enterTryOn() against a double-tap starting getUserMedia twice. */
let tryonEntering = false;

// --- perf overlay (?perf=1 or ?debug=1) ---
const dpr = window.devicePixelRatio;
let perfFrameCount = 0;
let perfLastSampleAt = -1;
let perfLastDetectCount = 0;

/** Cheap: increments a counter every frame; only builds/writes a string 2x/sec. */
function updatePerfOverlay(nowMs: number): void {
  if (perfLastSampleAt < 0) perfLastSampleAt = nowMs;
  perfFrameCount++;
  const elapsedMs = nowMs - perfLastSampleAt;
  if (elapsedMs < 500) return;

  const elapsedSec = elapsedMs / 1000;
  const fps = Math.round(perfFrameCount / elapsedSec);
  let detHz = "-";
  let infMs = "-";
  let delegateStr = "-";
  if (tracker) {
    const count = tracker.detectCount;
    detHz = String(Math.round((count - perfLastDetectCount) / elapsedSec));
    perfLastDetectCount = count;
    infMs = String(Math.round(tracker.avgInferenceMs));
    delegateStr = tracker.activeDelegate;
  }
  perfEl.textContent = `${fps}fps · det ${detHz}Hz · ${infMs}ms · ${delegateStr} · dpr${dpr}`;

  perfFrameCount = 0;
  perfLastSampleAt = nowMs;
}

// --- screen wake lock ---
// Edge-triggered (only calls acquire/release when the desired state actually
// CHANGES) so this can cheaply run every frame from loop() without spamming
// navigator.wakeLock.request() while e.g. a round runs for 30s straight.
let wakeLockHeld = false;

function updateWakeLock(): void {
  // Covers: a round in progress (phase==="round" is set by BOTH the solo and
  // online round-start paths), and the penalty/try-on stages (winner + loser
  // share one `penaltyStage` instance, so a null check covers either role).
  const shouldHold = phase === "round" || penaltyStage !== null || tryonActive;
  if (shouldHold === wakeLockHeld) return;
  wakeLockHeld = shouldHold;
  if (shouldHold) void acquireWakeLock();
  else releaseWakeLock();
}

const delay = (ms: number): Promise<void> => new Promise((r) => window.setTimeout(r, ms));

function showScreen(id: string): void {
  hideScreens();
  must<HTMLElement>(id).classList.remove("hidden");
}
function hideScreens(): void {
  for (const el of document.querySelectorAll<HTMLElement>(".screen")) el.classList.add("hidden");
}
function status(msg: string): void {
  statusEl.textContent = msg;
}

/** Win pips like "●○" up to ROUNDS_TO_WIN. */
function winDots(n: number): string {
  const w = Math.max(0, Math.min(ROUNDS_TO_WIN, Math.floor(n)));
  return "●".repeat(w) + "○".repeat(ROUNDS_TO_WIN - w);
}

// --- floating UI ---
function popup(text: string, fx: number, fy: number, color: string): void {
  const rect = gameEl.getBoundingClientRect();
  const div = document.createElement("div");
  div.className = "popup";
  div.textContent = text;
  div.style.left = `${rect.left + fx * rect.width}px`;
  div.style.top = `${rect.top + fy * rect.height}px`;
  div.style.color = color;
  popupsEl.appendChild(div);
  window.setTimeout(() => div.remove(), 950);
}
function toast(text: string): void {
  const div = document.createElement("div");
  div.className = "toast";
  div.textContent = text;
  popupsEl.appendChild(div);
  window.setTimeout(() => div.remove(), 1050);
}

// --- game loop (delta-time based) ---
function loop(ticker: Ticker): void {
  const nowMs = performance.now();
  if (perfMode) updatePerfOverlay(nowMs);
  updateWakeLock();
  // Thermal: only run detection while it can affect anything on screen. The
  // `loserPenaltyActive`/`tryonActive` exceptions keep the LOCAL tracker alive
  // past matchResult / on the try-on screen, since both read this same
  // tracker's landmarks (see startLoserPenaltyStage / startTryOnStage).
  if (phase === "round" || phase === "calibrate" || loserPenaltyActive || tryonActive) {
    if (mouthSource) mouthSource.update(nowMs);
    else if (tracker) tracker.update(nowMs);
  }
  renderer.layout();

  if (phase === "calibrate") {
    handleCalibrateFrame(nowMs);
    return;
  }

  if (phase === "round") {
    if (mode === "online") onlineRoundFrame(nowMs);
    else soloRoundFrame(ticker, nowMs);
    return;
  }

  // Idle screens. Online countdown number ticks from the server phase clock.
  if (mode === "online" && serverPhase === "countdown") updateOnlineCountdown(nowMs);
}

function handleCalibrateFrame(nowMs: number): void {
  if (tracker) calibrator.feed(tracker.jawOpen, tracker.lipGapRatio());
  calibFill.style.width = `${Math.round(calibrator.progress * 100)}%`;
  if (nowMs - calibStart >= CALIB_MS) finishCalibration();
}

function soloRoundFrame(ticker: Ticker, nowMs: number): void {
  const s = mouthSource ? mouthSource.sample() : null;
  if (s) sim.applyMouth("p1", s);

  accumulator += Math.min(ticker.deltaMS, 250);
  const events: SimEvent[] = [];
  while (accumulator >= TICK_MS) {
    for (const ev of sim.tick()) events.push(ev);
    accumulator -= TICK_MS;
  }
  for (const ev of events) handleEvent(ev);

  updateHud();
  renderer.render(sim.state, s, accumulator);
  handleFaceLost(nowMs);
}

function onlineRoundFrame(nowMs: number): void {
  const s = mouthSource ? mouthSource.sample() : null;
  // Optimistic local chomp: play the bite sound the instant our own FSM closes,
  // regardless of server confirmation (the score stays server-authoritative).
  if (s && s.closeSeq > prevLocalCloseSeq) {
    prevLocalCloseSeq = s.closeSeq;
    sfx.eat(0);
  }
  if (serverState) {
    const rs: RoundState = {
      running: true,
      elapsed: serverState.elapsed,
      duration: serverState.duration,
      foods: serverState.foods,
      players: {},
    };
    const extrap = net ? net.extrapolationMs(nowMs) : 0;
    renderer.render(rs, s, extrap);
    updateOnlineHud();
  }
  handleFaceLost(nowMs);
}

function handleEvent(ev: SimEvent): void {
  switch (ev.type) {
    case "eat":
      sfx.eat(ev.combo);
      renderer.burst(ev.x, ev.y, 0xffd166);
      popup(`+${ev.points}`, ev.x, ev.y, "#ffd166");
      if (ev.combo >= 3) toast(`콤보 x${ev.combo}!`);
      break;
    case "bomb":
      sfx.bomb();
      renderer.flash(0xffffff, 0.35, 180);
      renderer.burst(ev.x, ev.y, 0x9aa0a6);
      popup("-3", ev.x, ev.y, "#c9ccd1");
      break;
    case "burn":
      sfx.burn();
      renderer.flash(0xef4444, 0.25, BURN_MS);
      toast("매워!! 🌶️");
      break;
    case "miss":
      sfx.miss();
      break;
    case "end":
      endRound();
      break;
  }
}

function updateHud(): void {
  const st = sim.state;
  const p = st.players["p1"];
  scoreEl.textContent = String(p?.score ?? 0);
  const pct = st.duration > 0 ? Math.min(st.elapsed / st.duration, 1) * 100 : 0;
  timebarFill.style.width = `${pct}%`;
  const burning = (p?.burnUntil ?? 0) > st.elapsed;
  burnIndicator.classList.toggle("hidden", !burning);
}

function handleFaceLost(nowMs: number): void {
  if (!mouthSource) return;
  if (mouthSource.faceVisible) {
    faceLostSince = -1;
    facelost.classList.add("hidden");
  } else {
    if (faceLostSince < 0) faceLostSince = nowMs;
    if (nowMs - faceLostSince > FACE_LOST_MS) facelost.classList.remove("hidden");
  }
}

// --- solo phase transitions ---
function startCalibration(): void {
  calibrator = new Calibrator();
  calibText.textContent = "입을 크게 벌렸다 다물었다 2번 해주세요";
  calibGauge.classList.remove("hidden");
  calibFill.style.width = "0%";
  showScreen("screen-calibrate");
  calibStart = performance.now();
  phase = "calibrate";
}

function finishCalibration(): void {
  const res = calibrator.finish();
  if (!res || !tracker) {
    phase = "idle";
    calibText.textContent = "잘 안 됐어요, 다시!";
    window.setTimeout(() => startCalibration(), 900);
    return;
  }
  const fsm = new MouthFSM(res.tOpen, res.tClose);
  mouthSource = new FaceMouthSource(tracker, fsm, res.signal, (x, y) => renderer.videoNormToField(x, y));
  status(`입 신호: ${res.signal === "jaw" ? "턱" : "입술"}`);
  if (mode === "online") {
    // Ready-up: the server advances to countdown once both players are ready.
    phase = "idle";
    calibText.textContent = "준비 완료! 상대를 기다리는 중…";
    net?.send("ready");
  } else {
    void runCountdown();
  }
}

async function runCountdown(): Promise<void> {
  phase = "countdown";
  hud.classList.add("hidden");
  showScreen("screen-countdown");
  for (const n of [3, 2, 1]) {
    countdownNum.textContent = String(n);
    sfx.beep(n === 1);
    await delay(700);
  }
  startRound();
}

function startRound(): void {
  hideScreens();
  hud.classList.remove("hidden");
  myWinsEl.classList.add("hidden");
  burnIndicator.classList.add("hidden");
  facelost.classList.add("hidden");
  faceLostSince = -1;
  accumulator = 0;
  renderer.clearFoods();
  const seed = Math.floor(Math.random() * 2 ** 31);
  sim.start(seed, ROUND_MS, ["p1"]);
  phase = "round";
}

function endRound(): void {
  phase = "result";
  hud.classList.add("hidden");
  burnIndicator.classList.add("hidden");
  facelost.classList.add("hidden");
  renderer.clearFoods();
  sfx.fanfare();
  const score = sim.state.players["p1"]?.score ?? 0;
  resultScore.textContent = `점수 ${score}`;
  showScreen("screen-result");
}

// --- online flow ---
function onServerState(s: MatchState): void {
  serverState = s;
  const prev = serverPhase;
  serverPhase = s.phase;
  if (s.phase !== prev) handlePhaseTransition(s, prev);

  // Continuous, transition-independent UI.
  if (s.phase === "lobby") updateLobby(s);
  else if (s.phase === "roundResult") updateRoundResult(s);
  else if (s.phase === "matchResult") updateMatchResult(s);
}

function handlePhaseTransition(s: MatchState, prev: MatchPhase | null): void {
  // Any actual phase change (rematch proceeding to calibrate, a fresh join
  // resetting to lobby, etc.) leaves the penalty screen — tear it down here so
  // every path is covered uniformly; `enterOnlineMatchResult` below rebuilds
  // a fresh one when the new phase IS matchResult.
  teardownPenaltyStage();
  switch (s.phase) {
    case "lobby":
      hud.classList.add("hidden");
      oppHud.classList.add("hidden");
      teardownRtc(); // a fresh/different opponent may show up next
      phase = "idle";
      showScreen("screen-lobby");
      // The opponent's seat freed before the match ever really started (still
      // in calibrate — server-side pre-match reconnect semantics, see
      // arena-room.ts's onSeatExpired): the room stays matchable, so this
      // bounces back to the normal waiting UI instead of a forfeit result
      // screen — a toast explains why so it doesn't look like nothing happened.
      if (prev === "calibrate") toast("상대가 나가서 다시 기다리는 중… 🔄");
      break;
    case "calibrate":
      hud.classList.add("hidden");
      oppHud.classList.add("hidden");
      ensureRtcLink();
      void beginOnlineCalibration();
      break;
    case "countdown":
      hud.classList.add("hidden");
      oppHud.classList.add("hidden");
      countdownStartWall = performance.now();
      countdownNum.textContent = "3";
      phase = "idle";
      showScreen("screen-countdown");
      break;
    case "round":
      startOnlineRound();
      break;
    case "roundResult":
      enterOnlineRoundResult(s);
      break;
    case "matchResult":
      enterOnlineMatchResult(s);
      break;
  }
}

async function beginOnlineCalibration(): Promise<void> {
  if (mouseMode) {
    if (!mouthSource) mouthSource = new MouseMouthSource(gameEl);
    showScreen("screen-calibrate");
    calibText.textContent = "👆 터치 조작 · 준비 완료! 상대를 기다리는 중…";
    calibGauge.classList.add("hidden");
    phase = "idle";
    net?.send("ready");
    return;
  }
  showScreen("screen-calibrate");
  calibGauge.classList.remove("hidden");
  calibText.textContent = "카메라 준비 중…";
  try {
    await acquireCameraWithRetry({ audio: true });
    // rtcLink may still be under construction (async ICE-server fetch) —
    // attachLocalStreamIfReady() is also called once creation resolves, so
    // whichever of the two finishes last performs the actual attach.
    attachLocalStreamIfReady();
    startCalibration();
  } catch (err) {
    // Never leave the opponent wedged waiting on us: fall back to the mouse
    // source and proceed straight to ready-up.
    const permissionDenied = isPermissionDeniedError(err);
    status(`카메라/인식 실패: ${String(err)}`);
    if (!mouthSource) mouthSource = new MouseMouthSource(gameEl);
    toast(permissionDenied ? "카메라 권한이 없어서 화면 터치로 참여해요 👆" : "카메라를 못 써서 화면 터치로 참여해요 👆");
    calibGauge.classList.add("hidden");
    calibText.textContent = "👆 터치 조작 · 준비 완료! 상대를 기다리는 중…";
    phase = "idle";
    net?.send("ready");
  }
}

function startOnlineRound(): void {
  hideScreens();
  hud.classList.remove("hidden");
  oppHud.classList.remove("hidden");
  myWinsEl.classList.remove("hidden");
  burnIndicator.classList.add("hidden");
  facelost.classList.add("hidden");
  faceLostSince = -1;
  renderer.clearFoods();
  prevLocalCloseSeq = mouthSource?.sample()?.closeSeq ?? 0;
  phase = "round";
}

function updateOnlineCountdown(nowMs: number): void {
  const remain = COUNTDOWN_MS - (nowMs - countdownStartWall);
  const n = Math.max(1, Math.min(3, Math.ceil(remain / 1000)));
  const str = String(n);
  if (str !== countdownNum.textContent) {
    countdownNum.textContent = str;
    sfx.beep(n === 1);
  }
}

function opponentOf(
  s: MatchState,
): { name: string; score: number; wins: number; rematch: boolean } | null {
  if (!net) return null;
  const oppId = Object.keys(s.players).find((id) => id !== net?.myId);
  if (!oppId) return null;
  const o = s.players[oppId];
  return o ? { name: o.name, score: o.score, wins: o.wins, rematch: o.rematch } : null;
}

function updateOnlineHud(): void {
  const st = serverState;
  if (!st || !net) return;
  const me = st.players[net.myId];
  scoreEl.textContent = String(me?.score ?? 0);
  myWinsEl.textContent = winDots(me?.wins ?? 0);
  const pct = st.duration > 0 ? Math.min(st.elapsed / st.duration, 1) * 100 : 0;
  timebarFill.style.width = `${pct}%`;
  const burning = (me?.burnUntil ?? 0) > st.elapsed;
  burnIndicator.classList.toggle("hidden", !burning);

  const opp = opponentOf(st);
  oppName.textContent = opp?.name ?? "상대";
  oppScore.textContent = String(opp?.score ?? 0);
  oppWins.textContent = winDots(opp?.wins ?? 0);
}

function updateLobby(s: MatchState): void {
  const seated = Object.keys(s.players).length;
  if (lobbyRole === "host") {
    lobbyText.textContent = seated < 2 ? "친구를 기다리는 중…" : "상대 입장! 곧 시작해요";
    lobbyCode.textContent = displayCode ?? "-----";
    lobbyCode.classList.remove("hidden");
    lobbyHint.textContent = "이 코드를 친구에게 보내주세요";
  } else if (lobbyRole === "join") {
    lobbyText.textContent = "연결 중…";
    lobbyCode.textContent = displayCode ?? "-----";
    lobbyHint.textContent = "";
  } else {
    lobbyText.textContent = "상대를 찾는 중…";
    lobbyCode.classList.add("hidden");
    lobbyHint.textContent = "";
  }
}

function enterOnlineRoundResult(s: MatchState): void {
  phase = "idle";
  hud.classList.add("hidden");
  oppHud.classList.add("hidden");
  burnIndicator.classList.add("hidden");
  facelost.classList.add("hidden");
  renderer.clearFoods();
  sfx.fanfare();
  updateRoundResult(s);
  showScreen("screen-roundresult");
}

function updateRoundResult(s: MatchState): void {
  if (!net) return;
  const me = s.players[net.myId];
  const opp = opponentOf(s);
  if (s.lastRoundWinner === null) rrTitle.textContent = "무승부! 🤝";
  else if (s.lastRoundWinner === net.myId) rrTitle.textContent = "라운드 승리! 🎉";
  else rrTitle.textContent = "라운드 패배 😢";
  rrScore.textContent = `${me?.score ?? 0} : ${opp?.score ?? 0}`;
  rrWins.textContent = `${winDots(me?.wins ?? 0)}  vs  ${winDots(opp?.wins ?? 0)}`;
}

function enterOnlineMatchResult(s: MatchState): void {
  phase = "idle";
  hud.classList.add("hidden");
  oppHud.classList.add("hidden");
  renderer.clearFoods();
  updateMatchResult(s);
  const won = net ? s.matchWinner === net.myId : false;
  const trueDraw = s.matchWinner === null && s.aborted === null;
  if (won || trueDraw) sfx.fanfare();
  else sfx.miss();
  btnRematch.disabled = false;
  btnRematch.textContent = "다시 대결";
  showScreen("screen-matchresult");
}

function updateMatchResult(s: MatchState): void {
  if (!net) return;
  const me = s.players[net.myId];
  const opp = opponentOf(s);
  const amWinner = s.matchWinner === net.myId;
  const filter = isFilterId(s.penaltyFilter) ? s.penaltyFilter : null;

  if (s.aborted === "opponent-left") {
    mrTitle.textContent = "상대방이 나갔어요";
    teardownRtc(); // they're truly gone — stop the link (idempotent if already torn down)
  } else if (s.aborted === "calibrate-timeout") {
    mrTitle.textContent = "보정이 너무 오래 걸려 중단됐어요";
    teardownRtc();
  } else if (s.matchWinner === null) {
    mrTitle.textContent = "무승부! 🤝";
  } else if (amWinner) {
    mrTitle.textContent = "승리! 🏆 벌칙 필터를 골라주세요";
  } else {
    mrTitle.textContent = filter ? "벌칙 집행 중! ㅋㅋ" : "패배… 😱 벌칙을 기다리는 중";
  }
  mrWins.textContent = `${winDots(me?.wins ?? 0)}  vs  ${winDots(opp?.wins ?? 0)}`;
  mrOppRematch.classList.toggle("hidden", !opp?.rematch);
  refreshPenaltyUI(s);
}

function onServerEvt(ev: SimEvent): void {
  if (ev.type === "end") return;
  const own = net ? ("player" in ev && ev.player === net.myId) : false;
  switch (ev.type) {
    case "eat":
      renderer.burst(ev.x, ev.y, own ? 0xffd166 : 0x8899aa);
      if (own) {
        // No sfx.eat() here — the optimistic local chomp already played it the
        // instant our own FSM closed (onlineRoundFrame). Just the visuals.
        popup(`+${ev.points}`, ev.x, ev.y, "#ffd166");
        if (ev.combo >= 3) toast(`콤보 x${ev.combo}!`);
      }
      break;
    case "bomb":
      renderer.burst(ev.x, ev.y, 0x9aa0a6);
      if (own) {
        sfx.bomb();
        renderer.flash(0xffffff, 0.35, 180);
        popup("-3", ev.x, ev.y, "#c9ccd1");
      }
      break;
    case "burn":
      if (own) {
        sfx.burn();
        renderer.flash(0xef4444, 0.25, BURN_MS);
        toast("매워!! 🌶️");
      } else {
        renderer.burst(ev.x, ev.y, 0xef4444);
      }
      break;
    case "miss":
      // Neutral falling miss — no per-player sound in online mode.
      break;
  }
}

// --- Phase 3: WebRTC video/voice ---
// Video/voice is an ENHANCEMENT layer end to end: nothing here may block a
// game phase. All failures degrade to "no video" while the match continues.

/** Fetched once per online session; RtcLink tolerates the STUN-only default if this is slow/fails. */
async function fetchIceServers(): Promise<void> {
  try {
    const res = await fetch("/api/ice");
    if (!res.ok) return;
    const data = (await res.json()) as { iceServers?: RTCIceServer[] };
    if (Array.isArray(data.iceServers) && data.iceServers.length > 0) iceServers = data.iceServers;
  } catch {
    // Keep the STUN-only default.
  }
}

/**
 * Create the link once both players are seated (calibrate entry); reuse
 * across rounds/rematches. `RtcLink.create()` is async (it resolves ICE
 * servers BEFORE constructing the RTCPeerConnection — see rtc.ts), so this
 * guards against a second call racing it (`rtcLinkCreating`) and against a
 * teardown-during-creation resurrecting a stale link (`rtcEpoch`).
 */
function ensureRtcLink(): void {
  if (!net || !serverState) return;
  const dead = !rtcLink || rtcLink.connectionState === "failed" || rtcLink.connectionState === "closed";
  if (!dead || rtcLinkCreating) return;
  rtcLink?.close();
  rtcLink = null;
  const myEpoch = ++rtcEpoch;
  const mySlot = serverState.players[net.myId]?.slot;
  rtcLinkCreating = RtcLink.create({
    send: (payload) => net?.send("rtc", payload),
    onRemoteStream: attachRemoteStream,
    onConnectionState: updateRtcStatus,
    onRemoteTrackMuted: (muted) => rtcPaused.classList.toggle("hidden", !muted),
    // slot 2 is the polite peer (PLAN.md Phase 3 / PlayerMatchState.slot doc).
    polite: mySlot === 2,
    // Always re-fetch fresh rather than reusing the startOnline-time snapshot
    // — TURN credentials are ~1h lived, so a recreated/rematch link (and any
    // later reconnect()) should not ride out on a stale one.
    getIceServers: async () => {
      await fetchIceServers();
      return iceServers;
    },
  }).then((link) => {
    if (myEpoch !== rtcEpoch) {
      // Superseded by a teardown/new creation while we were building —
      // discard rather than resurrecting a stale link.
      link.close();
      return;
    }
    rtcLink = link;
    loggedConnectedPair = false;
    // Replay any signaling that arrived while the link was still under
    // construction — a dropped first offer would be a dead negotiation with
    // no retransmit.
    const queued = pendingRtcMessages;
    pendingRtcMessages = [];
    for (const msg of queued) void link.handleMessage(msg.payload, msg.from);
    attachLocalStreamIfReady();
  });
  void rtcLinkCreating.finally(() => {
    rtcLinkCreating = null;
  });
  rtcPip.classList.remove("hidden");
  // #btn-mic is revealed only once a local audio sender actually exists
  // (see attachLocalStreamIfReady) — hidden here for mouse-mode/camera-failure
  // players, whose setMicEnabled() would otherwise be a silent no-op.
  updateRtcStatus("connecting");
}

/**
 * Attach the captured local stream once BOTH the RtcLink and the camera
 * stream exist — called from wherever either one just became ready, since
 * camera acquisition (beginOnlineCalibration) and link creation (ensureRtcLink)
 * race each other and either can finish first.
 */
function attachLocalStreamIfReady(): void {
  if (!rtcLink || !camStream) return;
  rtcLink.addLocalStream(camStream);
  if (rtcLink.hasAudioSender()) btnMic.classList.remove("hidden");
}

function attachRemoteStream(stream: MediaStream): void {
  remoteStream = stream; // Phase 4: reused by the winner's penalty stage
  rtcPip.classList.remove("hidden");
  if (rtcVideo.srcObject !== stream) rtcVideo.srcObject = stream;
  // #rtc-video is `muted` in HTML so this autoplay always succeeds, including
  // on iOS (unmuted autoplay would be rejected there, leaving a black PIP).
  rtcVideo.play().catch((err: unknown) => console.warn("[rtc] remote video play() failed:", err));
  // There's audio to hear but the element is muted — offer the unmute chip.
  if (stream.getAudioTracks().length > 0 && rtcVideo.muted) {
    rtcUnmuteChip.classList.remove("hidden");
  }

  // Late-connecting RTC (the video track arrives after the winner already
  // entered the penalty stage and saw the "no camera" placeholder) — upgrade
  // to the real stage now. startWinnerPenaltyStage() is idempotent/no-ops if
  // we're not actually in that situation.
  if (serverState?.phase === "matchResult" && serverState.aborted === null && serverState.matchWinner === net?.myId) {
    void startWinnerPenaltyStage();
  }
}

function updateRtcStatus(state: ConnectionState): void {
  switch (state) {
    case "connecting":
      rtcStatus.textContent = "🟡 연결 중";
      break;
    case "connected":
      rtcStatus.textContent = "🟢 연결됨";
      void logConnectedDiagnostics();
      break;
    case "disconnected":
      rtcStatus.textContent = "⚪ 영상 없음";
      break;
    case "failed":
      rtcStatus.textContent = "⚪ 영상 연결 안 됨 · 게임은 계속돼요";
      void logFailedDiagnostics();
      break;
    case "closed":
      rtcStatus.textContent = "⚪ 영상 연결 안 됨 · 게임은 계속돼요";
      break;
  }
}

/** Logs the selected ICE candidate-pair types once per connection (diagnostics). */
async function logConnectedDiagnostics(): Promise<void> {
  if (loggedConnectedPair) return;
  const link = rtcLink;
  if (!link) return;
  const pair = await link.getSelectedCandidatePair();
  if (!pair || link !== rtcLink) return; // link may have been torn down while we awaited
  loggedConnectedPair = true;
  const label = `${pair.local}↔${pair.remote}`;
  console.info(`[rtc] connected via ${label}`);
  if (perfMode) rtcStatus.textContent = `🟢 연결됨 (${label})`;
}

/** Logs gathered local candidate type counts + a TURN hint on a hard failure (diagnostics). */
async function logFailedDiagnostics(): Promise<void> {
  const link = rtcLink;
  if (!link) return;
  const counts = await link.getLocalCandidateTypeCounts();
  if (link !== rtcLink) return;
  const summary = Object.entries(counts)
    .map(([k, v]) => `${k}:${v}`)
    .join(", ");
  console.warn(
    `[rtc] connection failed after ICE restart — gathered local candidates: ${summary || "없음"}. ` +
      "TURN 미설정이면 이종 네트워크 연결이 실패할 수 있음",
  );
}

function teardownRtc(): void {
  rtcEpoch++; // invalidate any in-flight RtcLink.create() so it can't adopt itself after this
  // Forget the in-flight promise too (rather than waiting for its own
  // .finally() to clear it) — otherwise an immediate re-entry into calibrate
  // (e.g. a fresh match landing right after this teardown) would see
  // `rtcLinkCreating` still set and skip starting a NEW creation. The old
  // promise still discards itself via the epoch check above when it settles.
  rtcLinkCreating = null;
  rtcLink?.close();
  rtcLink = null;
  remoteStream = null;
  pendingRtcMessages = [];
  loggedConnectedPair = false;
  rtcPip.classList.add("hidden");
  btnMic.classList.add("hidden");
  rtcPaused.classList.add("hidden");
  rtcUnmuteChip.classList.add("hidden");
  if (rtcVideo.srcObject) rtcVideo.srcObject = null;
}

function onServerRtc(payload: unknown): void {
  const p = payload as RtcSignal;
  const msg = { payload: p, from: p.from ?? "" };
  if (rtcLink) {
    void rtcLink.handleMessage(msg.payload, msg.from);
  } else if (rtcLinkCreating) {
    // The link is still being constructed (ICE-server fetch happens BEFORE
    // the RTCPeerConnection exists) — queue and replay once ready rather than
    // dropping a first offer with no retransmit.
    pendingRtcMessages.push(msg);
  }
  // else: no link and none being created — nothing relevant to deliver to.
}

// --- Phase 4: penalty AR filter stage ---
// Solo mode and draws never call any of this (see refreshPenaltyUI's `decisive`
// guard) — only a DECISIVE online match result (a real winner, not aborted)
// shows the filter strip / penalty stage. Winner and loser share ONE
// PenaltyStage instance (never both — a client is exactly one role).

/**
 * Driven by every matchResult state update (both the initial transition and
 * every subsequent broadcast, e.g. a filter switch or a rematch flag): shows
 * the right sub-UI for my role and keeps the stage's filter in sync.
 */
function refreshPenaltyUI(s: MatchState): void {
  const decisive = s.matchWinner !== null && s.aborted === null;
  if (!decisive) {
    teardownPenaltyStage();
    return;
  }
  rtcPip.classList.add("hidden"); // the enlarged stage replaces the small PIP
  const amWinner = net ? net.myId === s.matchWinner : false;
  const filter = isFilterId(s.penaltyFilter) ? s.penaltyFilter : null;

  if (amWinner) {
    mrFilterStrip.classList.remove("hidden");
    highlightSelected(filterButtons, filter);
    void startWinnerPenaltyStage();
    penaltyStage?.setFilter(filter);
  } else {
    mrFilterStrip.classList.add("hidden");
    if (filter) {
      // startLoserPenaltyStage() is async (awaits video dimensions before
      // ever creating the stage / a warp mesh off it — see its doc) and
      // applies the CURRENT serverState filter itself once it finishes, same
      // as startWinnerPenaltyStage()'s identical pattern just above. This
      // call still fires the (near-instant, common-case) live filter switch
      // immediately whenever the stage already exists.
      void startLoserPenaltyStage();
      penaltyStage?.setFilter(filter);
    } else {
      penaltyStageEl.classList.add("hidden");
      btnSnapshot.classList.add("hidden");
      hidePenaltyPlaceholder();
    }
  }
}

/** Shows the placeholder with caller-supplied text and hides the video stage + snapshot button. */
function showPenaltyPlaceholder(text: string): void {
  penaltyPlaceholder.textContent = text;
  penaltyPlaceholder.classList.remove("hidden");
  penaltyStageEl.classList.add("hidden");
  btnSnapshot.classList.add("hidden");
}

function hidePenaltyPlaceholder(): void {
  penaltyPlaceholder.classList.add("hidden");
}

/** Winner's stage: an independent SECOND tracker against the opponent's remote video. Idempotent. */
async function startWinnerPenaltyStage(): Promise<void> {
  if (penaltyStage || winnerStageStarting) return;
  if (!remoteStream || remoteStream.getVideoTracks().length === 0) {
    // No usable remote video (loser has no camera, or RTC never connected) —
    // still show the filter strip/rematch, just not the video stage itself.
    showPenaltyPlaceholder("상대 카메라가 없어요 🙈");
    return;
  }
  winnerStageStarting = true;
  hidePenaltyPlaceholder();
  const myEpoch = penaltyEpoch;
  penaltyVideo.classList.remove("mirrored");
  penaltyVideo.srcObject = remoteStream;
  try {
    await penaltyVideo.play();
    // Wait for real decoded dimensions before handing this video to a fresh
    // FaceLandmarker — starting its GPU self-test against a 0×0 video makes
    // detection fail for the wrong reason (same helper camera.ts uses).
    await waitForDimensions(penaltyVideo);
  } catch (err) {
    console.warn("[penalty] remote video play()/dimensions failed:", err);
  }

  let remoteTracker: FaceTracker | null = null;
  try {
    remoteTracker = await createFaceTracker(penaltyVideo);
  } catch (err) {
    console.warn("[penalty] remote tracker creation failed:", err);
  }
  winnerStageStarting = false;
  if (myEpoch !== penaltyEpoch) {
    // Superseded by a teardown (rematch/exit) while we were setting up.
    remoteTracker?.close();
    return;
  }

  penaltyStage = new PenaltyStage({
    elements: { container: penaltyStageEl, video: penaltyVideo, canvas: penaltyCanvas, hint: penaltyHint },
    mirrored: false,
    // null once hasFace flips false — FaceTrackerImpl otherwise keeps stale
    // landmarks around forever on face-loss (penalty.ts adds its own short
    // grace window on top so a single blink doesn't flicker the hint).
    getLandmarks: () => (remoteTracker?.hasFace ? remoteTracker.landmarks : null),
    driveTracker: remoteTracker ?? undefined,
  });
  penaltyStageEl.classList.remove("hidden");
  btnSnapshot.classList.remove("hidden");
  const filter = isFilterId(serverState?.penaltyFilter) ? serverState.penaltyFilter : null;
  penaltyStage.setFilter(filter);
  penaltyStage.start();
}

/**
 * Loser's stage: reads landmarks from the ALREADY-RUNNING session tracker —
 * no second detector. `loserPenaltyActive` (checked in the main loop) is what
 * actually keeps that tracker updating past matchResult. Idempotent.
 *
 * Awaits real decoded video dimensions on #penalty-video before creating the
 * stage, mirroring startWinnerPenaltyStage()'s identical wait — a warp filter
 * (if one's already selected server-side) would otherwise have its mesh/
 * texture created off a momentarily 0x0 video. Async, so re-invocations (this
 * is called on every matchResult broadcast, e.g. the winner switching
 * filters) are guarded by `loserStageStarting` the same way the winner path
 * guards itself with `winnerStageStarting`.
 */
async function startLoserPenaltyStage(): Promise<void> {
  if (penaltyStage || loserStageStarting) return;
  if (!camStream || !tracker) {
    // Mouse-mode fallback / camera never worked — nothing of ours to show.
    showPenaltyPlaceholder("카메라가 없어 벌칙 화면을 못 보여줘요 🙈");
    return;
  }
  loserStageStarting = true;
  // Same epoch guard as startWinnerPenaltyStage(): teardownPenaltyStage() (a
  // rematch, etc.) bumps this. A plain `penaltyStage === null` re-check isn't
  // enough here — camStream/tracker survive a rematch teardown, so a stale
  // in-flight call could otherwise resurrect a stage after teardown ran.
  const myEpoch = penaltyEpoch;
  hidePenaltyPlaceholder();
  penaltyVideo.classList.add("mirrored");
  penaltyVideo.srcObject = camStream;
  try {
    await penaltyVideo.play();
    await waitForDimensions(penaltyVideo);
  } catch (err) {
    console.warn("[penalty] loser video play()/dimensions failed:", err);
  }
  loserStageStarting = false;
  if (myEpoch !== penaltyEpoch || penaltyStage || !camStream || !tracker) return; // superseded/torn down while waiting
  const activeTracker = tracker;
  penaltyStage = new PenaltyStage({
    elements: { container: penaltyStageEl, video: penaltyVideo, canvas: penaltyCanvas, hint: penaltyHint },
    mirrored: true,
    // null once hasFace flips false (see the winner path's identical comment).
    getLandmarks: () => (activeTracker.hasFace ? activeTracker.landmarks : null),
  });
  penaltyStage.setStream(camStream);
  penaltyStageEl.classList.remove("hidden");
  btnSnapshot.classList.remove("hidden");
  loserPenaltyActive = true;
  const filter = isFilterId(serverState?.penaltyFilter) ? serverState.penaltyFilter : null;
  penaltyStage.setFilter(filter);
  penaltyStage.start();
}

function teardownPenaltyStage(): void {
  penaltyEpoch++; // discard any in-flight winner/loser-stage async setup
  winnerStageStarting = false;
  loserStageStarting = false;
  penaltyStage?.stop();
  penaltyStage = null;
  loserPenaltyActive = false;
  penaltyVideo.classList.remove("mirrored");
  penaltyStageEl.classList.add("hidden");
  penaltyPlaceholder.classList.add("hidden");
  mrFilterStrip.classList.add("hidden");
  btnSnapshot.classList.add("hidden");
  closeSnapshotFallback();
}

/** Web Share when available (with a File, called synchronously from the tap handler
 *  for transient activation); otherwise an <img> + [download] fallback overlay. */
function shareOrSaveSnapshot(stage: PenaltyStage | null): void {
  const blob = stage?.getLatestBlob();
  if (!blob) {
    // Blob refresh hasn't produced a frame yet (very fast tap / no face yet).
    toast("사진 준비 중이에요 — 잠깐 뒤 다시 눌러주세요 📸");
    return;
  }
  const file = new File([blob], "nyamduel.jpg", { type: "image/jpeg" });
  if (navigator.canShare?.({ files: [file] })) {
    // Files ONLY — no title/text. Share targets pick which fields to keep,
    // and several (KakaoTalk included) keep the TEXT and drop the file when
    // both are present, arriving as a text message instead of the photo.
    navigator.share({ files: [file] }).catch((err: unknown) => {
      // The user dismissing the share sheet is not a failure — don't also pop
      // the fallback overlay on top of a cancel they just chose.
      if (err instanceof Error && err.name === "AbortError") return;
      showSnapshotFallback(blob);
    });
  } else {
    showSnapshotFallback(blob);
  }
}

function showSnapshotFallback(blob: Blob): void {
  if (snapshotObjectUrl) URL.revokeObjectURL(snapshotObjectUrl);
  snapshotObjectUrl = URL.createObjectURL(blob);
  snapshotImg.src = snapshotObjectUrl;
  snapshotDownload.href = snapshotObjectUrl;
  snapshotFallback.classList.remove("hidden");
}

function closeSnapshotFallback(): void {
  snapshotFallback.classList.add("hidden");
  if (snapshotObjectUrl) {
    URL.revokeObjectURL(snapshotObjectUrl);
    snapshotObjectUrl = null;
  }
}

// --- Try-on mode: try the 5 penalty filters on yourself, fully local ---
// Reuses PenaltyStage exactly like the matchResult loser path (own camera,
// mirrored, landmarks from the shared session tracker) — the only difference
// is the filter is pure local UI state, never sent to a room.

async function enterTryOn(): Promise<void> {
  if (mouseMode) {
    toast("카메라가 필요한 기능이에요 📷");
    return;
  }
  if (tryonEntering || tryonStage) return; // already connecting or already running
  tryonEntering = true;
  setMenuDisabled(true);
  showScreen("screen-tryon");
  tryonFilter = null;
  highlightSelected(tryonFilterButtons, null);

  try {
    await ensureBooted();
    await acquireCameraWithRetry();
    await startTryOnStage();
  } catch (err) {
    if (isPermissionDeniedError(err)) {
      status("카메라 권한이 필요해요. 브라우저 주소창의 카메라 아이콘에서 허용해 주세요.");
    } else {
      status("카메라를 시작할 수 없어요 😢 뒤로 가서 다시 시도해 보세요");
    }
  } finally {
    tryonEntering = false;
  }
}

/**
 * Idempotent — a no-op if already running (e.g. re-entering after the camera was already up).
 * Awaits real decoded dimensions on #tryon-video first (mirrors startWinnerPenaltyStage()'s wait)
 * so a warp filter picked the instant this resolves doesn't build its mesh/texture off a 0x0
 * video; `tryonEpoch` guards against "back" tearing the screen down mid-wait.
 */
async function startTryOnStage(): Promise<void> {
  if (tryonStage || !camStream || !tracker) return;
  const myEpoch = tryonEpoch;
  tryonVideo.srcObject = camStream;
  try {
    await tryonVideo.play();
    await waitForDimensions(tryonVideo);
  } catch (err) {
    console.warn("[tryon] video play()/dimensions failed:", err);
  }
  if (myEpoch !== tryonEpoch || tryonStage || !camStream || !tracker) return; // superseded/torn down while waiting
  const activeTracker = tracker;
  tryonStage = new PenaltyStage({
    elements: { container: tryonStageEl, video: tryonVideo, canvas: tryonCanvas, hint: tryonHint },
    mirrored: true,
    // null once hasFace flips false (same MED-2 rationale as the matchResult loser path).
    getLandmarks: () => (activeTracker.hasFace ? activeTracker.landmarks : null),
  });
  tryonStage.setStream(camStream);
  tryonStage.setFilter(tryonFilter);
  tryonStage.setVisible(true);
  btnTryonSnapshot.classList.remove("hidden");
  tryonActive = true;
  tryonStage.start();
  status(`준비 완료 · ${activeTracker.activeDelegate}`);
}

/** Stops the stage + detection exception; deliberately does NOT touch camStream/tracker/video
 *  (kept alive so entering a game mode — or try-on again — right after is instant). */
function teardownTryOn(): void {
  tryonEpoch++; // discard any in-flight startTryOnStage() dimension-wait
  tryonStage?.setVisible(false);
  tryonStage?.stop();
  tryonStage = null;
  tryonActive = false;
  tryonEntering = false;
  btnTryonSnapshot.classList.add("hidden");
  closeSnapshotFallback();
}

// --- boot ---
let booted = false;

async function ensureBooted(): Promise<void> {
  if (booted) return;
  await renderer.init(gameEl);
  renderer.app.ticker.add(loop);
  await renderer.loadFoodTextures();
  booted = true;
}

/** Camera acquisition + tracker creation, each idempotent on retry. */
async function ensureCameraAndTracker(opts: { audio?: boolean } = {}): Promise<void> {
  if (!video) {
    status("카메라 준비 중…");
    const cam = await startCamera(opts);
    video = cam.video;
    camStream = cam.stream;
    renderer.attachVideo(video);
  }
  const v = video;
  if (!v) throw new Error("camera not ready");
  if (!tracker) {
    status("얼굴 인식 로딩 중…");
    tracker = await createFaceTracker(v);
    status(`준비 완료 · ${tracker.activeDelegate}`);
  }
}

const CAMERA_RETRY_ATTEMPTS = 3;
const CAMERA_RETRY_DELAY_MS = 800;

/** getUserMedia's permission-denial names (Firefox historically used the latter). */
function isPermissionDeniedError(err: unknown): boolean {
  return err instanceof DOMException && (err.name === "NotAllowedError" || err.name === "PermissionDeniedError");
}

/**
 * Auto-retries `ensureCameraAndTracker` up to CAMERA_RETRY_ATTEMPTS times with
 * a short delay between attempts, updating the status line per attempt.
 * `ensureCameraAndTracker`'s own `if (!video)` / `if (!tracker)` guards make
 * each sub-step idempotent, so a retry after a tracker-only failure reuses
 * the already-acquired camera stream instead of calling getUserMedia again
 * (the one-capture rule) — nothing here needs to track which sub-step failed.
 *
 * A permission denial (NotAllowedError/PermissionDeniedError) is NOT
 * retried — retrying can't fix a user decision — and is rethrown immediately
 * so the caller can show a permission-specific message.
 */
async function acquireCameraWithRetry(opts: { audio?: boolean } = {}): Promise<void> {
  let lastErr: unknown;
  for (let attempt = 1; attempt <= CAMERA_RETRY_ATTEMPTS; attempt++) {
    status(attempt === 1 ? "카메라 연결 중…" : `카메라 연결 재시도 중… (${attempt}/${CAMERA_RETRY_ATTEMPTS})`);
    try {
      await ensureCameraAndTracker(opts);
      return;
    } catch (err) {
      lastErr = err;
      if (isPermissionDeniedError(err)) throw err;
      if (attempt < CAMERA_RETRY_ATTEMPTS) await delay(CAMERA_RETRY_DELAY_MS);
    }
  }
  throw lastErr;
}

// --- solo entry ---
async function begin(): Promise<void> {
  teardownTryOn(); // no stuck tryonActive flag if a game mode is entered right after try-on
  mode = "solo";
  setMenuDisabled(true);
  sfx.unlockAudio();

  try {
    await ensureBooted();
  } catch (err) {
    status(`초기화 실패: ${String(err)}`);
    setMenuDisabled(false);
    return;
  }

  if (mouseMode) {
    if (!mouthSource) mouthSource = new MouseMouthSource(gameEl);
    status("👆 터치 조작 · 누르면 입 벌리고, 떼면 냠!");
    void runCountdown();
    return;
  }

  try {
    await acquireCameraWithRetry();
    startCalibration();
  } catch (err) {
    if (isPermissionDeniedError(err)) {
      status("카메라 권한이 필요해요. 브라우저 주소창의 카메라 아이콘에서 허용해 주세요.");
    } else {
      status("카메라를 시작할 수 없어요 😢 새로고침 후 다시 시도해 주세요");
    }
    setMenuDisabled(false);
  }
}

// --- online entry ---
function setMenuDisabled(v: boolean): void {
  btnStart.disabled = v;
  btnHost.disabled = v;
  btnJoinCode.disabled = v;
  btnRandom.disabled = v;
  btnTryon.disabled = v;
}

let mouthSendTickCount = 0;

function startMouthSendLoop(): void {
  if (mouthSendTimer >= 0) return;
  mouthSendTimer = window.setInterval(() => {
    if (mode !== "online" || !net) return;
    if (serverPhase !== "round" && serverPhase !== "calibrate") return;
    mouthSendTickCount++;
    // 20Hz during the round (every tick — eating precision needs it); 5Hz
    // during calibrate (every 4th tick — the server only needs a recent
    // lastSample before the round starts). Frees headroom in the shared
    // per-connection input budget for WebRTC signaling bursts (HIGH-2).
    if (serverPhase === "calibrate" && mouthSendTickCount % 4 !== 0) return;
    const s = mouthSource ? mouthSource.sample() : null;
    if (s) net.send("mouth", s);
  }, TICK_MS);
}

async function startOnline(kind: "host" | "join" | "random", code?: string): Promise<void> {
  teardownTryOn(); // no stuck tryonActive flag if a game mode is entered right after try-on
  mode = "online";
  setMenuDisabled(true);
  sfx.unlockAudio();
  void fetchIceServers(); // fire-and-forget; RtcLink (created later, at calibrate) tolerates the STUN-only default meanwhile

  try {
    await ensureBooted();
  } catch (err) {
    status(`초기화 실패: ${String(err)}`);
    backToStart();
    return;
  }

  net = new NetClient();
  const handlers: NetHandlers = { onState: onServerState, onEvt: onServerEvt, onStatus: status, onRtc: onServerRtc };
  lobbyRole = kind === "host" ? "host" : kind === "join" ? "join" : "random";
  displayCode = kind === "join" ? (code ?? null) : null;
  showScreen("screen-lobby");
  lobbyCode.classList.remove("hidden");
  lobbyText.textContent = kind === "random" ? "상대를 찾는 중…" : "연결 중…";

  try {
    if (kind === "host") displayCode = await net.host(handlers);
    else if (kind === "join") await net.joinCode(code ?? "", handlers);
    else await net.randomMatch(handlers);
    startMouthSendLoop();
    status("연결됨");
  } catch (err) {
    status(`연결 실패: ${String(err)}`);
    net?.leave();
    net = null;
    backToStart();
  }
}

function backToStart(): void {
  mode = "solo";
  phase = "idle";
  serverPhase = null;
  serverState = null;
  hud.classList.add("hidden");
  oppHud.classList.add("hidden");
  teardownPenaltyStage();
  teardownTryOn();
  teardownRtc();
  setMenuDisabled(false);
  showScreen("screen-start");
}

// --- wiring ---
btnStart.addEventListener("click", () => void begin());
btnAgain.addEventListener("click", () => void runCountdown());
btnRecalib.addEventListener("click", () => {
  if (mouseMode || !tracker) void runCountdown();
  else startCalibration();
});

btnHost.addEventListener("click", () => void startOnline("host"));
btnRandom.addEventListener("click", () => void startOnline("random"));
btnJoinCode.addEventListener("click", () => {
  joinCodeInput.value = "";
  showScreen("screen-joincode");
  joinCodeInput.focus();
});
btnJoinBack.addEventListener("click", () => showScreen("screen-start"));
btnJoinConfirm.addEventListener("click", () => {
  const code = normalizeRoomCode(joinCodeInput.value);
  if (!code) {
    joinCodeInput.classList.add("shake");
    status("코드는 5글자예요 (예: ABCDE)");
    window.setTimeout(() => joinCodeInput.classList.remove("shake"), 400);
    return;
  }
  void startOnline("join", code);
});
joinCodeInput.addEventListener("keydown", (e) => {
  if (e.key === "Enter") btnJoinConfirm.click();
});

btnRematch.addEventListener("click", () => {
  if (!net) return;
  net.send("rematch");
  btnRematch.disabled = true;
  btnRematch.textContent = "상대 기다리는 중…";
});
btnExit.addEventListener("click", () => {
  teardownPenaltyStage();
  teardownRtc();
  net?.leave();
  location.href = "/";
});
// Waiting in a lobby with no opponent must always be escapable — same clean
// full-reset path as 나가기 (frees the seat immediately, pre-match).
btnLobbyCancel.addEventListener("click", () => {
  teardownRtc();
  net?.leave();
  location.href = "/";
});

// filterButtons/tryonFilterButtons are wired at creation time (buildFilterStrip).
btnSnapshot.addEventListener("click", () => shareOrSaveSnapshot(penaltyStage));
btnSnapshotClose.addEventListener("click", closeSnapshotFallback);

btnTryon.addEventListener("click", () => void enterTryOn());
btnShare.addEventListener("click", () => {
  // Always share the canonical domain — KakaoTalk caches link previews PER
  // URL, and the legacy workers.dev address was scraped before OG tags
  // existed (cached as no-image). The canonical domain scrapes fresh.
  const shareUrl = "https://nyam.tikron.dev/";
  if (navigator.share) {
    // Called directly and synchronously from the tap handler (transient
    // activation) — same convention as shareOrSaveSnapshot() above.
    // URL only — the OG card already carries the title/description/image, so
    // an extra text blob just duplicates the message (2026-07-03 feedback).
    navigator.share({
      title: "냠냠대전",
      url: shareUrl,
    }).catch((err: unknown) => {
      // The user dismissing the share sheet is not a failure.
      if (err instanceof Error && err.name === "AbortError") return;
    });
    return;
  }
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(shareUrl).then(
      () => toast("링크가 복사됐어요! 친구에게 붙여넣기 📋"),
      () => {
        window.prompt("아래 링크를 복사하세요 👇", shareUrl);
        toast("링크가 복사됐어요! 친구에게 붙여넣기 📋");
      },
    );
  } else {
    window.prompt("아래 링크를 복사하세요 👇", shareUrl);
    toast("링크가 복사됐어요! 친구에게 붙여넣기 📋");
  }
});
btnTryonSnapshot.addEventListener("click", () => shareOrSaveSnapshot(tryonStage));
btnTryonBack.addEventListener("click", () => {
  teardownTryOn();
  setMenuDisabled(false);
  showScreen("screen-start");
});

btnMic.addEventListener("click", () => {
  micOn = !micOn;
  rtcLink?.setMicEnabled(micOn);
  btnMic.textContent = micOn ? "🎙️" : "🔇";
});
rtcUnmuteChip.addEventListener("click", () => {
  rtcVideo.muted = false;
  rtcVideo.play().then(
    () => rtcUnmuteChip.classList.add("hidden"),
    () => {
      /* still blocked — leave the chip visible so the user can tap again */
    },
  );
});

// The OS releases any held wake lock the instant the tab goes hidden —
// re-request it on return if we're still in a context that wants it (a
// round/penalty/try-on straddling the visibility change resumes it here;
// updateWakeLock()'s own edge-detection won't fire again since `phase`
// etc. didn't change, so this is the one place that has to re-issue it).
document.addEventListener("visibilitychange", () => {
  if (!document.hidden) reacquireWakeLockIfWanted();
});

// Own tab going background/foreground: pause the PIP overlay as a visual cue
// (the browser may throttle both our capture and the remote video's playback
// while hidden), and try exactly one reconnect on resume if the link died
// while we were away.
document.addEventListener("visibilitychange", () => {
  if (!rtcLink) return;
  if (document.hidden) {
    rtcPaused.classList.remove("hidden");
  } else {
    rtcPaused.classList.add("hidden");
    const st = rtcLink.connectionState;
    if (st === "failed" || st === "disconnected") rtcLink.reconnect();
  }
});

if (perfMode) perfEl.classList.remove("hidden");

showScreen("screen-start");
